import { useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { useSaveServer, errorMessage } from "../../hooks/useServers";

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

export function ServerForm({ projectId, server, servers = [], onDone, onCancel }) {
  const editing = !!server;
  // "linux" = SSH; "routeros" = MikroTik por su API (siempre a través de un servidor de salto Linux; nace en solo lectura y los cambios se permiten con el interruptor).
  const [type, setType] = useState(server?.type || "linux");
  const isOlt = type === "olt"; // OLT Huawei por Telnet (d50): como el MikroTik, siempre por un servidor de salto, con contraseña y sin base de datos; pero SOLO lectura
  const isRouter = type === "routeros" || isOlt; // equipos de red (MikroTik u OLT)
  const [jumpVia, setJumpVia] = useState(server?.jump_via || "");
  // Base de datos (consultas de solo lectura): datos NO secretos. Vacíos = sin configurar.
  const [dbDir, setDbDir] = useState(server?.db_dir || "");
  const [dbEnvVar, setDbEnvVar] = useState(server?.db_env_var || "");
  const [dbNameVar, setDbNameVar] = useState(server?.db_name_var || "");
  const [dbContainer, setDbContainer] = useState(server?.db_container || "");
  // Solo pueden ser puerta de salto los OTROS servidores del proyecto que no usen salto ellos mismos.
  const jumpOptions = servers.filter((s) => s.id !== server?.id && !s.jump_via && (s.type || "linux") === "linux");
  const usedAsJump = (server?.used_as_jump_by || []).length > 0;
  const [name, setName] = useState(server?.name || "");
  const [host, setHost] = useState(server?.host || "");
  const [port, setPort] = useState(String(server?.port || 22));
  const [username, setUsername] = useState(server?.username || (server?.type === "routeros" ? "" : "root"));
  const [authMethod, setAuthMethod] = useState(server?.auth_method || "password");
  const [secret, setSecret] = useState("");
  const [allowChanges, setAllowChanges] = useState(server?.access === "read_and_changes");
  const [notes, setNotes] = useState(server?.notes || "");
  const [error, setError] = useState("");
  const submitting = useRef(false); // anti doble clic: se activa en el mismo instante del clic
  const save = useSaveServer(projectId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting.current) return;
    setError("");
    const portNumber = Number(port);
    if (!Number.isInteger(portNumber)) {
      setError("El puerto debe ser un número.");
      return;
    }
    if (isRouter && !jumpVia) {
      setError(`${isOlt ? "Una OLT" : "Un RouterOS"} se consulta siempre a través de un servidor de salto: elige uno en «Conectar a través de».`);
      return;
    }
    submitting.current = true;
    try {
      await save.mutateAsync({
        serverId: server?.id,
        ...(editing ? {} : { type }),
        name,
        host,
        port: portNumber,
        username,
        auth_method: isRouter ? "password" : authMethod,
        secret: secret || undefined,
        access: allowChanges && !isOlt ? "read_and_changes" : "read_only",
        notes,
        jump_via: jumpVia, // "" = conexión directa
        ...(isRouter ? {} : { db_dir: dbDir, db_env_var: dbEnvVar, db_name_var: dbNameVar, db_container: dbContainer }),
      });
      setSecret("");
      onDone();
    } catch (err) {
      setError(errorMessage(err, "No se pudo guardar el servidor."));
    } finally {
      submitting.current = false;
    }
  };

  return (
    <form data-testid="server-form" onSubmit={handleSubmit} className="space-y-3">
      {!editing && (
        <div className="space-y-1.5">
          <Label className="text-xs">Tipo de equipo</Label>
          <select
            data-testid="server-type-select"
            className={SELECT_CLASS}
            value={type}
            onChange={(e) => {
              const next = e.target.value;
              setType(next);
              setPort(next === "routeros" ? "8728" : next === "olt" ? "23" : "22");
              setUsername(next === "routeros" || next === "olt" ? "" : "root");
            }}
          >
            <option value="linux">Servidor Linux (SSH)</option>
            <option value="routeros">MikroTik RouterOS (API)</option>
            <option value="olt">OLT Huawei (Telnet)</option>
          </select>
          {isOlt && (
            <p data-testid="olt-help" className="text-[11px] text-muted-foreground">
              La IA consulta la OLT por Telnet y siempre a través de un servidor de salto del proyecto (las OLT solo se alcanzan desde ahí). Por ahora es SOLO LECTURA:
              estado de tarjetas, ONT, potencias y alarmas; no autoriza, borra ni reinicia nada. Si la OLT rechaza el usuario o la clave, el panel NO reintenta y bloquea el equipo 5 minutos.
            </p>
          )}
          {isRouter && !isOlt && (
            <p data-testid="routeros-help" className="text-[11px] text-muted-foreground">
              La IA consulta el MikroTik por su API y siempre a través de un servidor de salto del proyecto. Para SOLO LECTURA, usa un
              usuario de un grupo de solo lectura (políticas «read» y «api», sin «write», «policy» ni «sensitive»): así el propio equipo
              impide escribir y ocultar contraseñas. Si más adelante activas «Permitir cambios», ese usuario necesita además «write» y «policy».
              Si rechaza el usuario o la clave, el panel NO reintenta y bloquea el equipo 5 minutos.
            </p>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Nombre del servidor</Label>
          <Input data-testid="server-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. WEB, CHR, VPS" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{isOlt ? "IP de la OLT (la que ve el servidor de salto)" : isRouter ? "IP del RouterOS (la que ve el servidor de salto)" : "Host o IP"}</Label>
          <Input data-testid="server-host-input" value={host} onChange={(e) => setHost(e.target.value)} placeholder="Ej. 203.0.113.10" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{isOlt ? "Puerto de Telnet (23)" : isRouter ? "Puerto de la API (8728)" : "Puerto SSH"}</Label>
          <Input data-testid="server-port-input" value={port} onChange={(e) => setPort(e.target.value)} inputMode="numeric" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{isOlt ? "Usuario de la OLT" : isRouter ? "Usuario de RouterOS" : "Usuario"}</Label>
          <Input data-testid="server-user-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder={isRouter ? "Ej. panel" : ""} />
        </div>
      </div>

      {!isRouter && (
      <div className="space-y-1.5">
        <Label className="text-xs">Cómo se autentica</Label>
        <select
          data-testid="server-auth-select"
          className={SELECT_CLASS}
          value={authMethod}
          onChange={(e) => setAuthMethod(e.target.value)}
        >
          <option value="password">Contraseña</option>
          <option value="key">Llave privada (sin frase de paso)</option>
        </select>
      </div>
      )}

      <div className="space-y-1.5">
        <Label className="text-xs">{isRouter ? "Conectar a través de (obligatorio)" : "Conectar a través de (opcional)"}</Label>
        <select
          data-testid="server-jump-select"
          className={SELECT_CLASS}
          value={jumpVia}
          onChange={(e) => setJumpVia(e.target.value)}
          disabled={usedAsJump}
        >
          <option value="">{isRouter ? "Elige el servidor de salto…" : "Ninguno (conexión directa)"}</option>
          {jumpOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <p className="text-[11px] text-muted-foreground">
          {usedAsJump
            ? `Este servidor es puerta de salto de ${server.used_as_jump_by.join(", ")}; no puede usar un salto a la vez.`
            : "Úsalo si este servidor solo acepta conexiones desde otro servidor del proyecto. El panel entra primero a ese y desde ahí llega a este. Solo un salto."}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">{!isRouter && authMethod === "key" ? "Llave privada" : "Contraseña"}</Label>
        {!isRouter && authMethod === "key" ? (
          <Textarea
            data-testid="server-secret-input"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            className="min-h-[90px] font-mono text-xs"
            placeholder={editing && server.has_secret ? "••• guardada (déjalo vacío para no cambiarla)" : "-----BEGIN OPENSSH PRIVATE KEY-----"}
            autoComplete="off"
          />
        ) : (
          <Input
            data-testid="server-secret-input"
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder={editing && server.has_secret ? "••• guardada (déjalo vacío para no cambiarla)" : ""}
            autoComplete="new-password"
          />
        )}
        <p className="text-[11px] text-muted-foreground">
          Se guarda cifrada y nunca se vuelve a mostrar. La IA tampoco la ve nunca.
        </p>
      </div>

      {isOlt ? (
        <p data-testid="olt-readonly-note" className="rounded-md border border-border p-3 text-[11px] text-muted-foreground">
          Las OLT son de solo lectura: el panel todavía no puede cambiar nada en ellas.
        </p>
      ) : (
        <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
          <div className="space-y-0.5">
            <Label className="text-xs">{isRouter ? "Permitir cambios (lo peligroso se confirma en el chat)" : "Permitir cambios (con confirmación en cada uno)"}</Label>
            <p data-testid="server-access-help" className="text-[11px] text-muted-foreground">
              {isRouter
                ? "Apagado = solo lectura. Encendido: la IA puede cambiar la configuración del MikroTik; los cambios menores (clientes, listas) se aplican y te avisa, y los de alto impacto (firewall, rutas, usuarios, reinicio…) te los explica y esperan tu «sí» en el chat. Nunca puede cortar el acceso del panel ni borrar la configuración."
                : "Apagado = solo lectura: la IA puede consultar pero no puede proponer cambios. Recomendado para proyectos con clientes reales."}
            </p>
          </div>
          <Switch data-testid="server-access-switch" checked={allowChanges} onCheckedChange={setAllowChanges} />
        </div>
      )}

      {!isRouter && (
      <div className="space-y-2 rounded-md border border-border p-3">
        <Label className="text-xs">Base de datos — opción avanzada: se detecta sola al pulsar «Analizar ahora» en el servidor</Label>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1">
            <Label className="text-[11px] text-muted-foreground">Carpeta del proyecto en el servidor (donde está el .env)</Label>
            <Input
              data-testid="server-db-dir-input"
              value={dbDir}
              onChange={(e) => setDbDir(e.target.value)}
              placeholder="Ej. /root/etweb_radius/backend"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Variable con la URL de Mongo</Label>
            <Input
              data-testid="server-db-envvar-input"
              value={dbEnvVar}
              onChange={(e) => setDbEnvVar(e.target.value)}
              placeholder="Ej. MONGO_URL"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Variable con el nombre de la base (opcional)</Label>
            <Input
              data-testid="server-db-namevar-input"
              value={dbNameVar}
              onChange={(e) => setDbNameVar(e.target.value)}
              placeholder="Ej. DB_NAME"
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] text-muted-foreground">Contenedor Docker de Mongo (solo si Mongo corre en Docker y el servidor no tiene mongosh)</Label>
          <Input
            data-testid="server-db-container-input"
            value={dbContainer}
            onChange={(e) => setDbContainer(e.target.value)}
            placeholder="Ej. mongodb"
          />
        </div>
        <p className="text-[11px] text-muted-foreground">
          No son secretos. La URL y la contraseña nunca salen del servidor: el panel solo le pide que lea su propio
          .env. Normalmente no hace falta tocar esto: el análisis lo llena solo.
        </p>
      </div>
      )}

      <div className="space-y-1.5">
        <Label className="text-xs">Notas (opcional)</Label>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej. Servidor web principal" />
      </div>

      {error && (
        <p data-testid="server-form-error" className="text-sm text-red-500">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={save.isPending}>
          Cancelar
        </Button>
        <Button data-testid="server-save-button" type="submit" disabled={save.isPending}>
          {save.isPending ? "Guardando..." : editing ? "Guardar cambios" : "Conectar servidor"}
        </Button>
      </div>
    </form>
  );
}
